import { ARTIFACT_TYPES } from '../../constants';
import { formatKpiValue, formatKpiDelta } from '../artifacts/kpiFormat';
import { parseArtifactData } from '../artifacts/parseArtifactData';

const serializeMarkdown = block => (typeof block.data === 'string' ? block.data : '');

const serializeColumnsRows = (columns, rows) => {
    if (!columns?.length || !rows?.length) return '';
    const headers = columns.map(column => column.label || column.name || column.id || '');
    const headerLine = `| ${headers.join(' | ')} |`;
    const separatorLine = `| ${headers.map(() => '---').join(' | ')} |`;
    const bodyLines = rows.map(row => {
        const cells = columns.map(column => {
            const key = column.name || column.id || column.label;
            const value = row?.[key];
            if (value === null || value === undefined) return '';
            return String(value).replace(/\|/g, '\\|');
        });
        return `| ${cells.join(' | ')} |`;
    });
    return [headerLine, separatorLine, ...bodyLines].join('\n');
};

// Structured blocks (table/widget/kpi) hold an object when streaming but a JSON
// string after reload, so normalize via parseArtifactData before reading fields.
const serializeTable = block => {
    const data = parseArtifactData(block.data) || {};
    const tableMarkdown = serializeColumnsRows(data.columns, data.rows);
    if (!tableMarkdown) return '';
    return data.title ? `${data.title}\n${tableMarkdown}` : tableMarkdown;
};

const serializeGrid = block => {
    const csvData = typeof block.data === 'string' ? block.data : '';
    return csvData.trim();
};

const serializeCode = block => {
    const { data } = block;
    const code = typeof data === 'string' ? data : data?.code || data?.content || '';
    if (!code) return '';
    return `\`\`\`python\n${code}\n\`\`\``;
};

const serializeWidget = block => {
    const data = parseArtifactData(block.data) || {};
    const title = data.title?.text || data.title || '';
    return title ? `[Chart: ${title}]` : '[Chart]';
};

const serializeKpi = block => {
    const parsed = parseArtifactData(block.data);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    if (!items.length) return '';
    return items
        .map(item => {
            const value = formatKpiValue(item.value, item.format, item.unit, { compact: false });
            const delta = formatKpiDelta(item.delta);
            const deltaSuffix = delta
                ? ` (${delta.text}${item.comparison_label ? ` ${item.comparison_label}` : ''})`
                : '';
            return `${item.label}: ${value}${deltaSuffix}`;
        })
        .join('\n');
};

const serializeMediaCollection = block => {
    const parsed = parseArtifactData(block.data);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    return items
        .map(item => [item.title, item.link_url].filter(Boolean).join(': '))
        .filter(Boolean)
        .join('\n');
};

const BLOCK_SERIALIZERS = {
    [ARTIFACT_TYPES.MARKDOWN]: serializeMarkdown,
    [ARTIFACT_TYPES.TABLE]: serializeTable,
    [ARTIFACT_TYPES.GRID]: serializeGrid,
    [ARTIFACT_TYPES.CODE]: serializeCode,
    [ARTIFACT_TYPES.WIDGET]: serializeWidget,
    [ARTIFACT_TYPES.KPI]: serializeKpi,
    [ARTIFACT_TYPES.MEDIA_COLLECTION]: serializeMediaCollection,
};

// Each serializer reads the block's resolved `data`. Reloaded blocks arrive with
// only `content_url`, so the caller must hydrate `data` first (see MessageFeedback).
export const serializeMessageForCopy = contentBlocks => {
    if (!Array.isArray(contentBlocks)) return '';
    return contentBlocks
        .map(block => {
            const serializer = BLOCK_SERIALIZERS[block?.type];
            return serializer ? serializer(block) : '';
        })
        .filter(part => part && part.length > 0)
        .join('\n\n');
};
